import type { Ref } from 'vue'; //
import { computed, inject, onMounted, onUnmounted, provide, ref } from 'vue';
import { useRouter } from 'vue-router';

interface SidebarContextType {
    isExpanded: Ref<boolean>;
    isMobileOpen: Ref<boolean>;
    isHovered: Ref<boolean>;
    activeItem: Ref<string | null>;
    openSubmenu: Ref<string | null>;
    toggleSidebar: () => void;
    toggleMobileSidebar: () => void;
    setIsHovered: (isHovered: boolean) => void;
    setActiveItem: (item: string | null) => void;
    toggleSubmenu: (item: string) => void;
}

const SidebarSymbol = Symbol();

export function useSidebarProvider() {
    const router = useRouter();

    const isExpanded = ref(true);
    const isMobileOpen = ref(false);
    const isMobile = ref(false);
    const isHovered = ref(false);
    const activeItem = ref<string | null>(null);
    const openSubmenu = ref<string | null>(null);

    const handleResize = () => {
        const mobile = window.innerWidth < 768;
        isMobile.value = mobile;
        if (!mobile) {
            isMobileOpen.value = false;
        }
    };

    onMounted(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
    });

    // Auto-close sidebar on route change (for mobile)
    onMounted(() => {
        router.afterEach(() => {
            isMobileOpen.value = false;
        });
    });

    const toggleSidebar = () => {
        if (isMobile.value) {
            isMobileOpen.value = !isMobileOpen.value;
        } else {
            isExpanded.value = !isExpanded.value;
        }
    };

    const toggleMobileSidebar = () => {
        isMobileOpen.value = !isMobileOpen.value;
    };

    const setIsHovered = (value: boolean) => {
        isHovered.value = value;
    };

    const setActiveItem = (item: string | null) => {
        activeItem.value = item;
    };

    const toggleSubmenu = (item: string) => {
        openSubmenu.value = openSubmenu.value === item ? null : item;
    };

    const context: SidebarContextType = {
        isExpanded: computed(() => (isMobile.value ? false : isExpanded.value)),
        isMobileOpen,
        isHovered,
        activeItem,
        openSubmenu,
        toggleSidebar,
        toggleMobileSidebar,
        setIsHovered,
        setActiveItem,
        toggleSubmenu,
    };

    provide(SidebarSymbol, context);

    return context;
}

export function useSidebar(): SidebarContextType {
    const context = inject<SidebarContextType>(SidebarSymbol);
    if (!context) {
        throw new Error('useSidebar must be used within a component that has SidebarProvider as an ancestor');
    }
    return context;
}
